import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCanvas } from '../../../../context/CanvasContext';
import ConfirmationModal from '../../../../../shared/components/modals/ConfirmationModal';
import EditModal from '../../../../../shared/components/modals/EditModal';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  fetchIncomeData,
  createIncome,
  updateIncome,
  deleteIncome,
  uploadIncomes,
  selectIncomes,
  selectIncomeLoading,
  selectIncomePagination,
  processManualDeposit,
} from '../../../../../../app/store/slices/incomeSlice';
import { currencyOptions } from '../../../../../../utils/constants/currencyOptions';
import { selectAccounts, fetchAccounts, updateAccount} from '../../../../../../app/store/slices/accountSlice';
import Pagination from '../../../../../shared/components/Pagination';
import { PAGE_SIZE_OPTIONS, INCOME_SOURCES, INCOME_FREQUENCIES } from '../../../../../../utils/constants/dashboardConstants';
import TableHeader from '../../../../../shared/components/TableHeader';
import TableBody from '../../../../../shared/components/TableBody';
import ActionButtons from '../../../../../shared/components/ActionButton';
import FileUploadButton from '../../../../../shared/components/FileUploadButton';
import FormInput from '../../../../../shared/ui/FormInput';
import FormSelect from '../../../../../shared/ui/FormSelect';
import {incomeTableColumns } from '../../../../../../utils/constants/dashboardConstants';
import useClickOutside from "../../../../../shared/hooks/useClickOutside";
import EditableCell from '../../../../../shared/components/EditableCell';
import InstructionsModal from '../../../../../shared/components/InstructionsModal'
import ManualDepositModal from '../../../../../shared/components/ManualDepositModal';
import ExpectedDepositForm from '../../../../../shared/components/ExpectedDepositForm';
import { FiPlay, FiClock } from 'react-icons/fi';
import { formatDateDMY } from '../../../../../../utils/formatDate';
import SummaryCard from "../../../../../shared/components/SummaryCard";
import { MdAccountBalanceWallet } from "react-icons/md";
import AddOptionsButton from "../../../../../shared/components/AddOptionsButton";
import {formatAmount} from '../../../../../../utils/formatAmount'; 
import {  fetchUserInsights
} from '../../../../../../app/store/slices/userDashboardSlice';


const IncomeContent = () => {
  const dispatch = useDispatch();
  const { selectedCanvas } = useCanvas();
  const incomes = useSelector(selectIncomes);
  const loading = useSelector(selectIncomeLoading);
  const pagination = useSelector(selectIncomePagination);
  const accounts = useSelector(selectAccounts);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [editIncomeData, setEditIncomeData] = useState({
    name: '',
    source: '',
    frequency: '',
    amount: '',
    currency: '',
    bonus: '',
    account: '',
    depositType: '',
    depositDay: '',
    depositMonth: '',
    lastDepositDate: ''
  });
  const [addIncomeData, setAddIncomeData] = useState({
    name: '',
    source: '',
    frequency: '',
    amount: '',
    currency: '',
    bonus: '',
    account: '',
    depositType: '',
    depositDay: '',
    depositMonth: '',
    lastDepositDate: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const fileInputRef = useRef();

  const [conversionRates, setConversionRates] = useState({});
  const [incomesWithBase, setIncomesWithBase] = useState([]);
  const [totalInBase, setTotalInBase] = useState(0);

  const baseCurrency = selectedCanvas?.currency || '';

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const addOptionsRef = useRef(null);
  useClickOutside(addOptionsRef, () => setShowAddOptions(false), showAddOptions);

  const [editingAmountId, setEditingAmountId] = useState(null);
  const [editingBonusId, setEditingBonusId] = useState(null);
  const [isUpdatingAmount, setIsUpdatingAmount] = useState(false);
  const [isUpdatingBonus, setIsUpdatingBonus] = useState(false);

  const [showInstructions, setShowInstructions] = useState(false)
  const [showManualDeposit, setShowManualDeposit] = useState(false);
  const [selectedIncomeForDeposit, setSelectedIncomeForDeposit] = useState(null);
  const [isProcessingDeposit, setIsProcessingDeposit] = useState(false);
  const insights = useSelector(state => state.userDashboard.insights);

  

  const calcTotalYearly = (amount, frequency, bonus = 0) => {
    const amt = Number(amount) || 0;
    const bon = Number(bonus) || 0;
    switch (frequency) {
      case 'Monthly': return amt * 12 + bon;
      case 'Weekly': return amt * 52 + bon;
      case 'Yearly': return amt + bon;
      case 'Daily': return amt * 365 + bon;
      case 'OneTime': return amt + bon;
      default: return amt + bon;
    }
  };

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchAccounts({ canvasId: selectedCanvas.id,  limit:1000 }));
      dispatch(fetchIncomeData({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
    }
  }, [dispatch, selectedCanvas?.id, currentPage, pageSize]);

  useEffect(() => {
    const fetchRates = async () => {
      if (!baseCurrency || !incomes?.length) {
        setConversionRates({});
        setTotalInBase(0);
        setIncomesWithBase([]);
        return;
      }
      const uniqueCurrencies = [
        ...new Set(incomes.map(inc => inc.currency).filter(cur => cur && cur !== baseCurrency))
      ];
      if (uniqueCurrencies.length === 0) {
        const incomesBase = incomes.map(inc => ({
          ...inc,
          baseIncome: calcTotalYearly(inc.amount, inc.frequency, inc.bonus)
        }));
        const total = incomesBase.reduce((sum, inc) => sum + inc.baseIncome, 0);
        setIncomesWithBase(incomesBase);
        setTotalInBase(total);
        setConversionRates({});
        return;
      }
      try {
        const res = await axios.get(
          `https://api.exchangerate-api.com/v4/latest/${baseCurrency}`,
        );
        const rates = res.data.rates || {};
        setConversionRates(rates);

        let total = 0;
        const incomesBase = incomes.map(inc => {
          const yearly = calcTotalYearly(inc.amount, inc.frequency, inc.bonus);
          let baseIncome = 0;
          if (inc.currency === baseCurrency) {
            baseIncome = yearly;
          } else if (rates[inc.currency]) {
            baseIncome = yearly * (1 / rates[inc.currency]);
          } else {
            baseIncome = 0;
          }
          total += baseIncome;
          return { ...inc, baseIncome };
        });
        setIncomesWithBase(incomesBase);
        setTotalInBase(total);
      } catch (err) {
        setTotalInBase(0);
        setConversionRates({});
        setIncomesWithBase([]);
      }
    };
    fetchRates();
  }, [incomes, baseCurrency]);

  useEffect(() => {
    if (showEditForm && selectedIncome) {
      setEditIncomeData({
        name: selectedIncome.name || '',
        source: selectedIncome.source || '',
        frequency: selectedIncome.frequency || '',
        amount: selectedIncome.amount || '',
        currency: selectedIncome.currency || '',
        bonus: selectedIncome.bonus || '',
        account: selectedIncome.account || '',
        depositType: selectedIncome.depositType || '',
        depositDay: selectedIncome.depositDay || '',
        depositMonth: selectedIncome.depositMonth || '',
        lastDepositDate: selectedIncome.lastDepositDate ? new Date(selectedIncome.lastDepositDate).toISOString().split('T')[0] : ''
      });
    }
  }, [showEditForm, selectedIncome]);

  // Handle Delete
  const handleDeleteIncome = async () => {
    if (!selectedIncome || !selectedCanvas?.id) {
      toast.error('Invalid Canvas ID');
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(
        deleteIncome({
          canvasId: selectedCanvas?.id,
          incomeId: selectedIncome?._id,
        })
      ).unwrap();
      dispatch(fetchIncomeData({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      setShowDeleteConfirm(false);
      setSelectedIncome(null);
      toast.success('Income Deleted Successfully');
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle Edit
  const handleEditIncome = async (e) => {
    e.preventDefault();
    if (!selectedIncome || !selectedCanvas?.id) {
      toast.error('Invalid Canvas ID');
      return;
    }
    setIsEditing(true);
    try {
      await dispatch(
        updateIncome({
          canvasId: selectedCanvas.id,
          incomeId: selectedIncome._id,
          updateData: editIncomeData,
        })
      ).unwrap();
      setShowEditForm(false);
      setSelectedIncome(null);
      dispatch(fetchIncomeData({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Income Updated Successfully');
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsEditing(false);
    }
  };

  // Handle Add
  const handleAddIncome = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id) {
      toast.error('Invalid Canvas ID');
      return;
    }
    setIsAdding(true);
    try {
      await dispatch(
        createIncome({
          canvasId: selectedCanvas.id,
          incomeData: addIncomeData,
        })
      ).unwrap();
      setShowAddForm(false);
      setAddIncomeData({
        name: '',
        source: '',
        frequency: '',
        amount: '',
        currency: '',
        bonus: '',
        account: ''
      });
      dispatch(fetchIncomeData({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Income Created Successfully');
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsAdding(false);
    }
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCanvas?.id) {
      toast.error('Invalid Canvas ID or file');
      return;
    }
    try {
      await dispatch(uploadIncomes({ canvasId: selectedCanvas.id, file })).unwrap();
      dispatch(fetchIncomeData({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Incomes Imported Successfully');
    } catch (err) {
      toast.error(err?.message || err);
    }
  };
  
  const handleAmountClick = (income) => {
    setEditingAmountId(income._id);
  };

  const handleSaveAmount = async (income, newValue) => {
    setIsUpdatingAmount(true);
    try {
      await dispatch(
        updateIncome({
          canvasId: selectedCanvas.id,
          incomeId: income._id,
          updateData: {
            name: income.name,
            source: income.source,
            frequency: income.frequency,
            amount: Number(newValue) || 0,
            currency: income.currency,
            bonus: income.bonus,
            account: income.account
          },
        })
      ).unwrap();
      
      setEditingAmountId(null);
      dispatch(fetchIncomeData({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Amount updated successfully');
      
    } catch (err) {
      toast.error('Failed to update amount');
    } finally {
      setIsUpdatingAmount(false);
    }
  };

  const handleDiscardAmount = () => {
    setEditingAmountId(null);
  };

  const handleBonusClick = (income) => {
    setEditingBonusId(income._id);
  };

  const handleSaveBonus = async (income, newValue) => {
    setIsUpdatingBonus(true);
    try {
      await dispatch(
        updateIncome({
          canvasId: selectedCanvas.id,
          incomeId: income._id,
          updateData: {
            name: income.name,
            source: income.source,
            frequency: income.frequency,
            amount: income.amount,
            currency: income.currency,
            bonus: Number(newValue) || 0,
            account: income.account
          },
        })
      ).unwrap();
      
      setEditingBonusId(null);
      dispatch(fetchIncomeData({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success('Bonus updated successfully');
    } catch (err) {
      toast.error('Failed to update bonus');
    } finally {
      setIsUpdatingBonus(false);
    }
  };

  const handleDiscardBonus = () => {
    setEditingBonusId(null);
  };

  const handleDownloadTemplate = () => {
    try {
      const baseCurrency = selectedCanvas?.currency || 'USD';
      const sampleAccountName = accounts.length > 0 ? accounts[0].name : 'account_name_here';
      
      const templateData = [
        { 
          name: 'Monthly Salary', 
          source: 'Salary', 
          frequency: 'Monthly', 
          amount: '8000', 
          currency: baseCurrency, 
          bonus: '1000', 
          account: sampleAccountName,
          lastDepositDate: '2025-01-15'
        },
        { 
          name: 'Daily Income', 
          source: 'Business', 
          frequency: 'Daily', 
          amount: '100', 
          currency: baseCurrency, 
          bonus: '0', 
          account: sampleAccountName,
          lastDepositDate: '2025-01-10'
        },
        { 
          name: 'Weekly Allowance', 
          source: 'Other', 
          frequency: 'Weekly', 
          amount: '500', 
          currency: baseCurrency, 
          bonus: '0', 
          account: sampleAccountName,
          lastDepositDate: '2025-01-08'
        },
        { 
          name: 'OneTime Bonus', 
          source: 'Other', 
          frequency: 'OneTime', 
          amount: '5000', 
          currency: baseCurrency, 
          bonus: '0', 
          account: sampleAccountName,
          lastDepositDate: '2025-12-25'
        }
      ];

      const csvContent = [
        'name,source,frequency,amount,currency,bonus,account,lastDepositDate',
        ...templateData.map(row => 
          `${row.name},${row.source},${row.frequency},${row.amount},${row.currency},${row.bonus},${row.account},${row.lastDepositDate}`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `income_template_${baseCurrency}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setShowInstructions(false);
    } catch (error) {
      toast.error('Failed to download template');
      console.error('Template download error:', error);
    }
  };

  // Manual Deposit Handler
  const handleManualDeposit = async (depositData) => {
    setIsProcessingDeposit(true);
    try {
      await dispatch(processManualDeposit({
        canvasId: selectedCanvas.id,
        incomeId: depositData.incomeId,
        depositData: {
          actualAmount: depositData.actualAmount,
          depositDate: depositData.depositDate,
          accountId: depositData.accountId
        }
      })).unwrap();

      setShowManualDeposit(false);
      setSelectedIncomeForDeposit(null);
      dispatch(fetchUserInsights(selectedCanvas?.id));
      toast.success(`${depositData.actualAmount} deposited successfully!`);
      
      // Refresh data
      dispatch(fetchAccounts({ canvasId: selectedCanvas.id, limit: 1000 }));
      dispatch(fetchIncomeData({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
    } catch (error) {
      toast.error('Failed to process deposit');
    } finally {
      setIsProcessingDeposit(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-6">Income</h1>
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        {/* Header Section  */}
        <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 md:gap-8 mb-6">
          {/* Overview Card Row */}
          <div className="flex flex-wrap gap-4 md:gap-8 w-full md:w-auto">
             <SummaryCard
              title="Total Monthly Income"
              value={baseCurrency
                    ? `${formatAmount(insights?.totalIncome)} ${baseCurrency}`
                    : 'Auto calculated'}
              icon={<MdAccountBalanceWallet className="w-5 h-5" />}
              valueColor="text-green-700"
              iconBg="bg-green-100"
              iconColor="text-green-600"
              formatValue={val=> val}
            />

          </div>
          {/* Add Income Button */}
         <AddOptionsButton
            showOptions={showAddOptions}
            setShowOptions={setShowAddOptions}
            addOptionsRef={addOptionsRef}
            onAddManual={() => setShowAddForm(true)}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            onShowInstructions={() => setShowInstructions(true)}
            buttonLabel="Add Income"
            buttonTitle="Add Income"
            manualLabel="Add Manually"
            FileUploadButtonComponent={FileUploadButton}
          />
        </div>
        {/* Table */}
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <TableHeader columns={incomeTableColumns} />
                <TableBody
                  loading={loading}
                  data={incomesWithBase}
                  columnsCount={11}
                  emptyMessage="No Income found."
                  loadingMessage="Loading Incomes..."
                  renderRow={income => (
                    <tr key={income?._id}>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{income?.name}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{income?.source}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{income?.frequency}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          <EditableCell
                            value={income?.amount || 0}
                            isEditing={editingAmountId === income._id}
                            isUpdating={isUpdatingAmount}
                            onEdit={() => handleAmountClick(income)}
                            onSave={(newValue) => handleSaveAmount(income, newValue)}
                            onCancel={handleDiscardAmount}
                            inputType="number"
                            inputProps={{
                              min: "0",
                              step: "0.01"
                            }}
                            displayFormat={(val) => formatAmount(val) || '-'}
                            editTitle="Click to edit amount"
                          />
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          <EditableCell
                            value={income?.bonus || 0}
                            isEditing={editingBonusId === income._id}
                            isUpdating={isUpdatingBonus}
                            onEdit={() => handleBonusClick(income)}
                            onSave={(newValue) => handleSaveBonus(income, newValue)}
                            onCancel={handleDiscardBonus}
                            inputType="number"
                            inputProps={{
                              min: "0",
                              step: "0.01"
                            }}
                            displayFormat={(val) => formatAmount(val) ||  '-'}
                            editTitle="Click to edit bonus"
                          />
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{income?.currency}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatAmount(calcTotalYearly(income?.amount, income?.frequency, income?.bonus))}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {income.baseIncome !== undefined
                            ? `${formatAmount(income?.baseIncome)} ${baseCurrency}`
                            : '-'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {accounts.find(a => a._id === income.account)?.name || "-"}
                        </td>
                        
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(() => {
                              if (income?.lastDepositDate) {
                                return (
                                  <div className="flex items-center gap-1">
                                    <FiClock className="w-3 h-3 text-green-500" />
                                    <span className="text-xs text-green-700">
                                      {formatDateDMY(income?.lastDepositDate)}
                                    </span>
                                  </div>
                                );
                              } else {
                                return <span className="text-gray-400 text-xs">Not set</span>;
                              }
                          })()}
                        </td>

                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => {
                              setSelectedIncomeForDeposit({
                                ...income,
                                accountName: accounts.find(a => a._id === income.account)?.name
                              });
                              setShowManualDeposit(true);
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs transition-colors"
                            title="Manual Deposit"
                          >
                            <FiPlay className="w-3 h-3" />
                            Deposit
                          </button>

                          <ActionButtons
                            onEdit={() => {
                              setSelectedIncome(income);
                              setShowEditForm(true);
                            }}
                            onDelete={() => {
                              setSelectedIncome(income);
                              setShowDeleteConfirm(true);
                            }}
                          />
                        </td>
                      </tr>
                    )}
                    />
              </table>
              {/* Pagination */}
              <Pagination
                pagination={pagination}
                pageSize={pageSize}
                setPageSize={setPageSize}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                PAGE_SIZE_OPTIONS={PAGE_SIZE_OPTIONS}
                label="Incomes per page"
                totalLabel="total Incomes"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteConfirm}
        title="Delete Income"
        message={`Are you sure you want to delete "${selectedIncome?.name}"? This action cannot be undone.`}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedIncome(null);
        }}
        onConfirm={handleDeleteIncome}
        loading={isDeleting}
      />
      {/* Edit Modal */}
      <EditModal
        show={showEditForm}
        title="Edit Income"
        onCancel={() => {
          setShowEditForm(false);
          setSelectedIncome(null);
        }}
        onSubmit={handleEditIncome}
        loading={isEditing}
        submitLabel="Update"
      >
        <div className="space-y-4">
          <FormInput
            label="Income Label"
            type="text"
            value={editIncomeData.name}
            onChange={e => setEditIncomeData({ ...editIncomeData, name: e.target.value })}
            required
          />
          <FormSelect
            label="Source"
            value={editIncomeData.source}
            onChange={e => setEditIncomeData({ ...editIncomeData, source: e.target.value })}
            required
          >
            <option value="">Select Source</option>
            {INCOME_SOURCES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Frequency"
            value={editIncomeData.frequency}
            onChange={e => setEditIncomeData({ ...editIncomeData, frequency: e.target.value })}
            required
          >
            <option value="">Select Frequency</option>
            {INCOME_FREQUENCIES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          {/*
          <FormInput
            label="Amount"
            type="number"
            value={editIncomeData.amount}
            onChange={e => setEditIncomeData({ ...editIncomeData, amount: e.target.value })}
            required
            min="0"
            step="0.01"
          />
          */}
          <FormSelect
            label="Currency"
            value={editIncomeData.currency}
            onChange={e => setEditIncomeData({ ...editIncomeData, currency: e.target.value })}
            required
          >
            <option value="">Select Currency</option>
            {currencyOptions.map(opt => (
              <option key={opt.code} value={opt.code}>
                {opt.code} - {opt.name}
              </option>
            ))}
          </FormSelect>
          {/*
          <FormInput
            label="Bonus"
            type="number"
            value={editIncomeData.bonus}
            onChange={e => setEditIncomeData({ ...editIncomeData, bonus: e.target.value })}
            min="0"
            step="0.01"
          />
          */}
          <FormSelect
            label="Deposit Account"
            value={editIncomeData.account}
            onChange={e => setEditIncomeData({ ...editIncomeData, account: e.target.value })}
            required
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>
                {acc.name} ({acc.currency}) - Balance: {formatAmount(acc?.balance)}
              </option>
            ))}
          </FormSelect>
          <ExpectedDepositForm
            depositType={editIncomeData.depositType}
            setDepositType={(value) => setEditIncomeData({ ...editIncomeData, depositType: value })}
            depositDay={editIncomeData.depositDay}
            setDepositDay={(value) => setEditIncomeData({ ...editIncomeData, depositDay: value })}
            depositMonth={editIncomeData.depositMonth}
            setDepositMonth={(value) => setEditIncomeData({ ...editIncomeData, depositMonth: value })}
            depositDate={editIncomeData.lastDepositDate}
            setDepositDate={(value) => setEditIncomeData({ ...editIncomeData, lastDepositDate: value })}
            frequency={editIncomeData.frequency}
          />
        </div>
      </EditModal>

      <EditModal
        show={showAddForm}
        title="Add Income"
        onCancel={() => setShowAddForm(false)}
        onSubmit={handleAddIncome}
        loading={isAdding}
        submitLabel="Add"
      >
        <div className="space-y-4">
          <FormInput
            label="Income"
            type="text"
            value={addIncomeData.name}
            onChange={e => setAddIncomeData({ ...addIncomeData, name: e.target.value })}
            required
          />
          <FormSelect
            label="Source"
            value={addIncomeData.source}
            onChange={e => setAddIncomeData({ ...addIncomeData, source: e.target.value })}
            required
          >
            <option value="">Select Source</option>
            {INCOME_SOURCES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Frequency"
            value={addIncomeData.frequency}
            onChange={e => setAddIncomeData({ ...addIncomeData, frequency: e.target.value })}
            required
          >
            <option value="">Select Frequency</option>
            {INCOME_FREQUENCIES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormInput
            label="Amount"
            type="number"
            value={addIncomeData.amount}
            onChange={e => setAddIncomeData({ ...addIncomeData, amount: e.target.value })}
            required
            min="0"
            step="0.01"
          />
          <FormSelect
            label="Currency"
            value={addIncomeData.currency}
            onChange={e => setAddIncomeData({ ...addIncomeData, currency: e.target.value })}
            required
          >
            <option value="">Select Currency</option>
            {currencyOptions.map(opt => (
              <option key={opt.code} value={opt.code}>
                {opt.code} - {opt.name}
              </option>
            ))}
          </FormSelect>
          <FormInput
            label="Bonus"
            type="number"
            value={addIncomeData.bonus}
            onChange={e => setAddIncomeData({ ...addIncomeData, bonus: e.target.value })}
            min="0"
            step="0.01"
          />
          <FormSelect
            label="Deposit Account"
            value={addIncomeData.account}
            onChange={e => setAddIncomeData({ ...addIncomeData, account: e.target.value })}
            required
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>
                {acc.name} ({acc.currency}) - Balance: {formatAmount(acc.balance)}
              </option>
            ))}
          </FormSelect>
          <ExpectedDepositForm
            depositType={addIncomeData.depositType}
            setDepositType={(value) => setAddIncomeData({ ...addIncomeData, depositType: value })}
            depositDay={addIncomeData.depositDay}
            setDepositDay={(value) => setAddIncomeData({ ...addIncomeData, depositDay: value })}
            depositMonth={addIncomeData.depositMonth}
            setDepositMonth={(value) => setAddIncomeData({ ...addIncomeData, depositMonth: value })}
            depositDate={addIncomeData.lastDepositDate}
            setDepositDate={(value) => setAddIncomeData({ ...addIncomeData, lastDepositDate: value })}
            frequency={addIncomeData.frequency}
          />
        </div>
      </EditModal>
      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        type="income"
        onDownloadTemplate={handleDownloadTemplate}
      />
      <ManualDepositModal
        show={showManualDeposit}
        onClose={() => {
          setShowManualDeposit(false);
          setSelectedIncomeForDeposit(null);
        }}
        income={selectedIncomeForDeposit}
        onConfirm={handleManualDeposit}
        loading={isProcessingDeposit}
      />
    </div>
  );
};

export default IncomeContent;